import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Star, Quote, ThumbsUp, MessageSquare } from "lucide-react";
import { TESTIMONIALS } from "../mock";

const REVIEW_SOURCES = [
  { source: "Google", rating: 4.8, count: 1247, color: "#4285F4" },
  { source: "G2", rating: 4.7, count: 482, color: "#FF492C" },
  { source: "Trustpilot", rating: 4.9, count: 890, color: "#00B67A" },
  { source: "Glassdoor", rating: 4.6, count: 314, color: "#0CAA41" }
];

const BREAKDOWN = [
  { star: 5, pct: 78 }, { star: 4, pct: 16 }, { star: 3, pct: 4 }, { star: 2, pct: 1 }, { star: 1, pct: 1 }
];

const EXTENDED_REVIEWS = [
  ...TESTIMONIALS,
  { name: "Sandeep Reddy", role: "DevOps Lead, FinTech", avatar: "https://images.unsplash.com/photo-1463453091185-61582044d556?w=200&q=80", text: "Passed CKA on first attempt. The labs were extremely well-designed and instructors were always available." },
  { name: "Aishwarya Pillai", role: "Product Manager, Healthcare", avatar: "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=200&q=80", text: "The Design Thinking program shifted how our entire product team approaches discovery. Outstanding facilitation." }
];

const ReviewsPage = () => {
  const avg = (REVIEW_SOURCES.reduce((s, r) => s + r.rating, 0) / REVIEW_SOURCES.length).toFixed(1);
  const total = REVIEW_SOURCES.reduce((s, r) => s + r.count, 0);
  return (
    <div className="bg-white min-h-screen">
      <Header/>
      <PageHero eyebrow="REVIEWS" title="What learners &amp; enterprises think" subtitle={`Over ${total.toLocaleString()} verified reviews across leading platforms.`}/>

      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 bg-[#15348C] text-white rounded-2xl p-8 text-center">
              <div className="text-7xl font-bold text-[#8BD835]" style={{fontFamily:"Times New Roman, serif"}}>{avg}</div>
              <div className="flex justify-center gap-1 mt-2">
                {[1,2,3,4,5].map(i => <Star key={i} size={18} fill="#F57C20" className="text-[#F57C20]"/>)}
              </div>
              <div className="text-sm text-gray-200 mt-2">Based on {total.toLocaleString()} verified reviews</div>
            </div>

            <div className="lg:col-span-2 bg-white border border-gray-100 rounded-2xl p-8">
              <h3 className="text-[#15348C] mb-5">Rating breakdown</h3>
              <div className="space-y-3">
                {BREAKDOWN.map(b => (
                  <div key={b.star} className="flex items-center gap-3">
                    <span className="w-10 text-sm font-semibold text-[#15348C]">{b.star}★</span>
                    <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full bg-[#6BBE32] transition-all duration-700" style={{width:`${b.pct}%`}}/>
                    </div>
                    <span className="w-12 text-right text-sm text-gray-500">{b.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Sources */}
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {REVIEW_SOURCES.map((r, i) => (
            <Reveal key={r.source} variant="up" delay={i * 60}>
              <div className="sg-card bg-white border border-gray-100 rounded-2xl p-5 text-center">
                <div className="font-bold" style={{color:r.color}}>{r.source}</div>
                <div className="mt-2 text-3xl font-bold text-[#15348C]" style={{fontFamily:"Times New Roman, serif"}}>{r.rating}<span className="text-base text-gray-400">/5</span></div>
                <div className="text-xs text-gray-500 mt-1">{r.count.toLocaleString()} reviews</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">VERIFIED REVIEWS</div>
              <h2 className="text-[#15348C]">Read what real learners have to say</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {EXTENDED_REVIEWS.map((t, i) => (
              <Reveal key={i} variant="up" delay={(i % 6) * 50}>
                <div className="sg-card bg-white border border-gray-100 rounded-2xl p-6 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex gap-1">{[1,2,3,4,5].map(s => <Star key={s} size={14} fill="#F57C20" className="text-[#F57C20]"/>)}</div>
                    <Quote size={20} className="text-[#6BBE32]"/>
                  </div>
                  <p className="text-gray-700 leading-relaxed flex-1">“{t.text}”</p>
                  <div className="mt-5 pt-4 border-t border-gray-100 flex items-center gap-3">
                    <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover"/>
                    <div>
                      <div className="font-semibold text-[#15348C]">{t.name}</div>
                      <div className="text-xs text-gray-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={100}>
            <div className="mt-10 text-center">
              <button className="inline-flex items-center gap-2 bg-[#15348C] hover:bg-[#0A1F5C] text-white font-semibold px-6 py-3 rounded-md transition-colors sg-btn-glow">
                <MessageSquare size={16}/> Write a Review
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer/>
    </div>
  );
};

export default ReviewsPage;
